import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Gift, Loader2, Lock, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  checkReferralCode,
  getStoredReferralInfo,
  setManualReferral,
  setStoredReferral,
} from "@/lib/boide-api";
import { cn } from "@/lib/utils";

type CheckStatus = "idle" | "checking" | "valid" | "invalid" | "unknown";

/** Cùng quy tắc `_CODE_RE` bên BE — sai định dạng thì khỏi gọi mạng. */
const CODE_RE = /^[a-z0-9](?:[a-z0-9._-]{1,30})[a-z0-9]$/;

/**
 * State ô mã giới thiệu ở màn đăng nhập/đăng ký.
 *
 * Mục tiêu: ca bấm link giới thiệu trên mạng xã hội mà `?ref=` không tới được (trình
 * duyệt trong app, copy nhầm link, mở ở máy khác) — người dùng vẫn tự gõ mã được.
 *
 * 🔴 Mã đến từ LINK thì KHOÁ CỨNG (`locked`): ô hiện read-only, không có nút xoá, và
 * `setManualReferral` từ chối ghi đè. Mã tự gõ thì sửa/xoá thoải mái.
 *
 * ⚠️ Đọc `?ref=` NGAY TẠI ĐÂY, không chờ `__root.tsx`: effect của component con chạy
 * TRƯỚC effect của cha, nên vào thẳng `/login?ref=abc` thì lúc hook này đọc storage,
 * root chưa kịp lưu và ô sẽ hiện trống (rồi mở khoá cho gõ đè). `setStoredReferral`
 * idempotent + giữ mã khoá cũ nên gọi hai lần không sao.
 */
export function useReferralInput() {
  const [code, setCode] = useState("");
  const [locked, setLocked] = useState(false);
  const [status, setStatus] = useState<CheckStatus>("idle");
  const seq = useRef(0);

  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("ref");
    if (fromUrl) setStoredReferral(fromUrl);
    const stored = getStoredReferralInfo();
    if (stored) {
      setCode(stored.code);
      // Mã từ link không kiểm lại (effect debounce bỏ qua khi `locked`): người dùng
      // không sửa được nó, hiện ✗ chỉ gây hoang mang. Mã gõ tay thì effect kia kiểm.
      setLocked(stored.locked);
    }
  }, []);

  const runCheck = useCallback(async (value: string): Promise<CheckStatus> => {
    const clean = value.trim().toLowerCase();
    const id = ++seq.current;
    if (!clean) {
      setStatus("idle");
      return "idle";
    }
    if (!CODE_RE.test(clean)) {
      setStatus("invalid");
      return "invalid";
    }
    setStatus("checking");
    let next: CheckStatus;
    try {
      next = (await checkReferralCode(clean)).valid ? "valid" : "invalid";
    } catch {
      // Lỗi mạng ≠ mã sai. Không chặn đăng ký vì một lần kiểm hỏng.
      next = "unknown";
    }
    if (id === seq.current) setStatus(next);
    return next;
  }, []);

  // Debounce kiểm mã khi người dùng gõ.
  useEffect(() => {
    if (locked) return;
    const t = window.setTimeout(() => void runCheck(code), 450);
    return () => window.clearTimeout(t);
  }, [code, locked, runCheck]);

  const change = (value: string) => {
    if (locked) return;
    const clean = value.replace(/\s+/g, "").toLowerCase().slice(0, 32);
    setCode(clean);
    // Lưu NGAY: đăng nhập Google/Apple trên iOS là redirect rời trang, lúc quay về
    // `completeLogin` chỉ đọc được mã từ storage.
    setManualReferral(clean);
    setStatus(clean ? "checking" : "idle");
  };

  /**
   * Gọi trước khi đăng ký / bắt đầu OAuth. Trả `false` (kèm lý do) nếu người dùng đã gõ
   * một mã KHÔNG tồn tại — để họ sửa, thay vì đăng ký xong mới biết mã không ăn.
   */
  const ensureValid = async (): Promise<{ ok: boolean; message?: string }> => {
    if (locked || !code.trim()) return { ok: true };
    const result = status === "valid" ? "valid" : await runCheck(code);
    if (result === "invalid") {
      return {
        ok: false,
        message: "Mã giới thiệu không tồn tại. Kiểm tra lại hoặc xoá mã để tiếp tục.",
      };
    }
    return { ok: true };
  };

  /**
   * Bản ĐỒNG BỘ cho nút Google/Apple: trình duyệt chỉ cho mở popup OAuth trong đúng
   * nhịp bấm của người dùng, chen một `await` mạng vào trước là popup bị chặn. Dựa vào
   * kết quả debounce đã có; đang kiểm dở thì cho qua (BE vẫn bỏ qua mã sai).
   */
  const blockingError = (): string | null =>
    !locked && code.trim() && status === "invalid"
      ? "Mã giới thiệu không tồn tại. Kiểm tra lại hoặc xoá mã để tiếp tục."
      : null;

  return { code, locked, status, change, ensureValid, blockingError };
}

export type ReferralInput = ReturnType<typeof useReferralInput>;

export function ReferralCodeField({
  referral,
  disabled,
  defaultOpen = false,
}: {
  referral: ReferralInput;
  disabled?: boolean;
  defaultOpen?: boolean;
}) {
  const { code, locked, status, change } = referral;
  const [open, setOpen] = useState(defaultOpen);
  const expanded = open || locked || !!code;

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="mx-auto flex min-h-9 items-center gap-1.5 text-xs font-medium text-primary underline-offset-4 hover:underline"
      >
        <Gift className="size-3.5" aria-hidden="true" />
        Có mã giới thiệu? Nhập tại đây
      </button>
    );
  }

  return (
    <div className="auth-form">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="referral-code">Mã giới thiệu {locked ? "" : "(không bắt buộc)"}</Label>
        <div className="relative">
          <Input
            id="referral-code"
            value={code}
            onChange={(e) => change(e.target.value)}
            readOnly={locked}
            aria-readonly={locked}
            disabled={disabled && !locked}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="Nhập mã người giới thiệu"
            className={cn(locked && "cursor-not-allowed opacity-90")}
            aria-describedby="referral-code-hint"
          />
          <span className="pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-center">
            {locked ? (
              <Lock className="size-4 text-primary" aria-label="Mã đã khoá" />
            ) : status === "checking" ? (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            ) : status === "valid" ? (
              <Check className="size-4 text-emerald-500" aria-label="Mã hợp lệ" />
            ) : status === "invalid" ? (
              <X className="size-4 text-destructive" aria-label="Mã không tồn tại" />
            ) : null}
          </span>
        </div>
        <p
          id="referral-code-hint"
          className={cn(
            "text-[11px] leading-4",
            status === "invalid" && !locked ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {locked
            ? "Mã đã được gắn từ link giới thiệu và không thể thay đổi."
            : status === "invalid"
              ? "Mã không tồn tại — kiểm tra lại chính tả."
              : status === "valid"
                ? "Mã hợp lệ — sẽ gắn vào tài khoản ngay khi bạn đăng nhập/đăng ký."
                : "Gắn vào tài khoản ngay khi đăng nhập/đăng ký (nếu tài khoản chưa có người giới thiệu), và không đổi được sau đó."}
        </p>
      </div>
    </div>
  );
}
